import React, { useMemo } from 'react';
import { Package, PlaneTakeoff, BarChart3, Landmark } from 'lucide-react';

// Each card has its own tint, carried by the icon tile.
const TONES = {
  blue:   { tile: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300' },
  violet: { tile: 'bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300' },
  sky:    { tile: 'bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300' },
  amber:  { tile: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300' },
};

const Card = ({ icon: Icon, tone, value, title, label }) => {
  return (
    <div className="rounded-2xl p-4 sm:p-[18px] bg-white dark:bg-[#111a2e]
                    border border-slate-200/80 dark:border-slate-700/50
                    shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)]
                    transition-shadow">
      <div className="flex items-center gap-3">
        <span className={`w-11 h-11 rounded-xl grid place-items-center flex-shrink-0 ${TONES[tone].tile}`}>
          <Icon size={20} />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-[21px] sm:text-[25px] leading-[1.15] font-extrabold text-slate-900 dark:text-white truncate"
             title={String(title ?? value)}>
            {value}
          </p>
          <p className="text-[12.5px] font-medium text-slate-500 dark:text-slate-400 truncate">{label}</p>
        </div>
      </div>
    </div>
  );
};

/** Two related figures in one card, split down the middle. */
const DualCard = ({ icon: Icon, tone, left, right }) => (
  <div className="rounded-2xl p-4 sm:p-[18px] bg-white dark:bg-[#111a2e]
                  border border-slate-200/80 dark:border-slate-700/50
                  shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)]
                  transition-shadow">
    <div className="flex items-center gap-3">
      <span className={`w-11 h-11 rounded-xl grid place-items-center flex-shrink-0 ${TONES[tone].tile}`}>
        <Icon size={20} />
      </span>

      <div className="min-w-0 flex-1 grid grid-cols-2 divide-x divide-slate-200/80 dark:divide-slate-700/50">
        {[left, right].map((col) => (
          <div key={col.label} className="min-w-0 first:pr-3.5 last:pl-3.5">
            <p className="text-[18px] sm:text-[21px] leading-[1.15] font-extrabold text-slate-900 dark:text-white truncate"
               title={col.title || String(col.value)}>
              {col.value}
            </p>
            <p className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-slate-400 dark:text-slate-500 truncate">
              {col.label}
            </p>
          </div>
        ))}
      </div>

    </div>
  </div>
);

/** Totals across whatever list the page is currently showing. */
const UserStatCards = ({ shipments = [] }) => {
  const stats = useMemo(() => {
    const num = (v) => {
      if (v === null || v === undefined || v === '-' || v === '') return 0;
      const parts = Array.isArray(v) ? v : String(v).split(/\s*&\s*/);
      return parts.reduce((a, p) => {
        const n = Number(String(p).replace(/[₹$,\s]/g, ''));
        return a + (Number.isFinite(n) ? n : 0);
      }, 0);
    };

    const today = new Date(); today.setHours(0, 0, 0, 0);

    // Flight dates reach the row already formatted as dd/mm/yyyy, and a
    // consignment flown in legs carries several joined with "&".
    const isToday = (value) => String(value ?? '')
      .split(/\s*&\s*/)
      .some((part) => {
        const m = part.trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
        if (!m) return false;
        const d = new Date(+m[3], +m[2] - 1, +m[1]);
        return d.getTime() === today.getTime();
      });

    // A shipment departs when its flight goes; that is the same event the
    // tracking timeline logs as DEPARTED, and the flight date is already here
    // — reading it off the rows costs nothing, where asking the tracking API
    // would be one call per AWB and only for the carriers it covers.
    const departed = shipments.filter((s) => isToday(s?.flightDate ?? s?.flightDates)).length;

    return {
      total: shipments.length,
      departed,
      fob: shipments.reduce((a, s) => a + num(s.fobValue), 0),
      dbk: shipments.reduce((a, s) => a + num(s.dbk ?? s.dbkClaim), 0),
      rodtep: shipments.reduce((a, s) => a + num(s.rodtep ?? s.rodtepAmt), 0),
    };
  }, [shipments]);

  // Lakhs and crores, so a crore-sized FOB total still fits the card.
  const money = (n) => {
    const v = Math.round(n);
    if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
    if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
    return `₹${v.toLocaleString('en-IN')}`;
  };
  const exact = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
  const compact = (n) => {
    const v = Math.round(n);
    if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
    if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
    if (v >= 1e4) return `₹${(v / 1e3).toFixed(1)} K`;
    return `₹${v.toLocaleString('en-IN')}`;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
      <Card icon={Package}      tone="blue"   value={stats.total}    label="Total Shipments" />
      <Card icon={PlaneTakeoff} tone="violet" value={stats.departed} label="Departed Today" />
      <Card icon={BarChart3}    tone="sky"    value={money(stats.fob)} title={exact(stats.fob)} label="Total FOB Value" />
      <DualCard
        icon={Landmark}
        tone="amber"
        left={{ label: 'DBK', value: compact(stats.dbk), title: exact(stats.dbk) }}
        right={{ label: 'RoDTEP', value: compact(stats.rodtep), title: exact(stats.rodtep) }}
      />
    </div>
  );
};

export default UserStatCards;
